import axios from 'axios';
import i18next from 'i18next';
import { api, entityNames, featureAccess } from 'config/constants';
import { options } from 'helpers/cookieHelper';
import { handleVersion } from 'helpers/versionsHelper';
import { showToast } from 'helpers/toast';
import { createReplacePatch } from 'helpers/patchHelper';
import * as type from './types';
import { updateUser } from './user';

// The sent state of a Request Access click, kept per stack for the session.
export const INSPECT_ACCESS_REQUESTED_PREFIX = 'inspectAccessRequested:';

export const REQUEST_STATUS = {
  SENT: 'sent',
  ALREADY: 'alreadyRequested',
};

// One request per user and stack at a time: key is '<userUUID>:<accountUUID>'.
var inFlight = {};
// Bumped by clearInspectAccessRequested(); a request sent before a bump
// settles as stale (no stored key, no toast).
var sessionGeneration = 0;

export function inspectAccessRequestedKey(uuid) {
  return INSPECT_ACCESS_REQUESTED_PREFIX + uuid;
}

export function readInspectAccessRequested(uuid) {
  try {
    var raw = sessionStorage.getItem(inspectAccessRequestedKey(uuid));
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    var status = parsed && parsed.status;
    if (status === REQUEST_STATUS.SENT || status === REQUEST_STATUS.ALREADY) {
      return status;
    }
  } catch (e) {
    // storage unavailable or junk JSON: treated as not requested
  }
  return null;
}

export function clearInspectAccessRequested() {
  sessionGeneration += 1;
  inFlight = {};
  try {
    var keys = [];
    for (var i = 0; i < sessionStorage.length; i++) {
      var key = sessionStorage.key(i);
      if (key && key.indexOf(INSPECT_ACCESS_REQUESTED_PREFIX) === 0) {
        keys.push(key);
      }
    }
    keys.forEach(function (key) {
      sessionStorage.removeItem(key);
    });
  } catch (e) {
    // storage unavailable
  }
}

function requestErrorMessage(err) {
  var response = err && err.response;
  var body = response && response.data;
  if (response && response.status === 422) {
    var dataErrors =
      body && body.validationErrors && body.validationErrors.dataErrors;
    return (dataErrors && dataErrors.feature) || i18next.t('messages:genericError');
  }
  if (response && response.status === 400 && body && body.errorCode === 104) {
    return i18next.t('inspectLanding:errorNotMember');
  }
  if (response && response.status === 503) {
    return i18next.t('inspectLanding:errorUnavailable');
  }
  return i18next.t('messages:genericError');
}

function trimmed(value) {
  return typeof value === 'string' ? value.trim() : '';
}

// Client-side check of the optional contact fields; a blank field is fine.
// Returns { contactEmail?, contactPhone? } holding translation keys.
export function validateContact(contact) {
  var out = {};
  var email = trimmed(contact && contact.contactEmail);
  var phone = trimmed(contact && contact.contactPhone);
  if (email) {
    var parts = email.split('@');
    var bad =
      email.length > 254 ||
      /\s/.test(email) ||
      parts.length !== 2 ||
      parts[0].length === 0 ||
      parts[1].indexOf('.') === -1 ||
      parts[1].charAt(0) === '.' ||
      parts[1].charAt(parts[1].length - 1) === '.';
    if (bad) out.contactEmail = 'inspectLanding:errEmailInvalid';
  }
  if (phone) {
    var digits = (phone.match(/[0-9]/g) || []).length;
    if (phone.length > 40 || !/^[0-9 +\-().x]+$/.test(phone) || digits < 7) {
      out.contactPhone = 'inspectLanding:errPhoneInvalid';
    }
  }
  return out;
}

// The 422 field errors for the contact fields, or null when the response is
// not a contact-field 422 (a feature error always wins).
function contactFieldErrors(err) {
  var response = err && err.response;
  if (!response || response.status !== 422) return null;
  var body = response.data;
  var dataErrors =
    body && body.validationErrors && body.validationErrors.dataErrors;
  if (!dataErrors || dataErrors.feature) return null;
  var out = {};
  var found = false;
  ['contactEmail', 'contactPhone'].forEach(function (name) {
    if (dataErrors[name]) {
      out[name] = dataErrors[name];
      found = true;
    }
  });
  return found ? out : null;
}

// Asks the API to tell the Slate Pages team that the user wants Inspect on
// the current stack, with optional contact details. Resolves
// { status, requestedTimestamp }; rejects with an Error whose message is safe
// to show in the dialog (and, for contact field 422s, a dataErrors map).
export function requestInspectAccess(contact) {
  return function (dispatch, getState) {
    var state = getState();
    var accountUUID =
      state.accounts &&
      state.accounts.currentAccount &&
      state.accounts.currentAccount.uuid;
    if (!accountUUID) {
      return Promise.reject(new Error(i18next.t('messages:genericError')));
    }
    var userUUID = (state.currentUser && state.currentUser.uuid) || '';
    var key = userUUID + ':' + accountUUID;
    if (inFlight[key]) return inFlight[key];

    var body = { feature: featureAccess.INSPECT };
    ['contactEmail', 'contactPhone'].forEach(function (name) {
      var value = trimmed(contact && contact[name]);
      if (value) body[name] = value;
    });

    var generation = sessionGeneration;
    dispatch({
      type: type.FETCH_START,
      entityName: entityNames.POST_FEATURE_ACCESS_REQUEST,
    });

    var promise = axios
      .post(
        api.GET_ACCOUNT + '/' + accountUUID + '/feature-access-requests',
        body,
        options,
      )
      .then(function (res) {
        handleVersion(res);
        var data = (res.data && res.data.data) || {};
        var status =
          data.status === REQUEST_STATUS.ALREADY
            ? REQUEST_STATUS.ALREADY
            : REQUEST_STATUS.SENT;
        if (inFlight[key] === promise) delete inFlight[key];
        dispatch({
          type: type.FETCH_SUCCESS,
          entityName: entityNames.POST_FEATURE_ACCESS_REQUEST,
        });
        if (generation === sessionGeneration) {
          try {
            sessionStorage.setItem(
              inspectAccessRequestedKey(accountUUID),
              JSON.stringify({ status: status }),
            );
          } catch (e) {
            // storage unavailable: the sent state just does not persist
          }
          if (status === REQUEST_STATUS.SENT) {
            showToast.success(i18next.t('inspectLanding:toastSent'));
          }
        }
        return { status: status, requestedTimestamp: data.requestedTimestamp };
      })
      .catch(function (err) {
        if (inFlight[key] === promise) delete inFlight[key];
        var fieldErrors = contactFieldErrors(err);
        if (fieldErrors) {
          dispatch({
            type: type.FETCH_ERROR,
            entityName: entityNames.POST_FEATURE_ACCESS_REQUEST,
            hasErrors: { dataErrors: fieldErrors },
          });
          var fieldError = new Error(i18next.t('messages:genericError'));
          fieldError.dataErrors = fieldErrors;
          return Promise.reject(fieldError);
        }
        dispatch({
          type: type.FETCH_ERROR,
          entityName: entityNames.POST_FEATURE_ACCESS_REQUEST,
        });
        return Promise.reject(new Error(requestErrorMessage(err)));
      });
    inFlight[key] = promise;
    return promise;
  };
}

// The seen flag is sent at most once per user per session: a failed write is not retried until a reload.
var seenSent = {};

// Silent PATCH of /dashboardSettings/inspectLandingSeen once the visitor has spent time on the landing page.
export function markInspectLandingSeen() {
  return function (dispatch, getState) {
    var user = getState().currentUser || {};
    if (
      !user.uuid ||
      (user.dashboardSettings && user.dashboardSettings.inspectLandingSeen === true) ||
      seenSent[user.uuid]
    ) {
      return Promise.resolve(false);
    }
    seenSent[user.uuid] = true;
    return dispatch(
      updateUser(
        { patches: [createReplacePatch('/dashboardSettings/inspectLandingSeen', true)] },
        entityNames.DASHBOARD_SETTINGS,
        { silent: true },
      ),
    );
  };
}

// For tests: forget which users the seen flag was sent for.
export function resetInspectLandingSeenForTests() {
  seenSent = {};
}
